import { RefObject, useContext, useEffect, useRef, useState } from 'react'

import { generateId } from 'core-components/helpers/generateId'
import { PortalContext } from 'core-components/library/popup'

import { POPUP_PARENT_IDS_ATTRIBUTE, POPUP_PARENT_IDS_SPLITTER } from '../constants'
import { handleByClick } from '../helpers/handleBy'
import { TVisibilityTrigger } from '../interface/popup'

interface IUseCloseByClickHandler {
  visible: boolean
  rootRef: RefObject<HTMLDivElement | null>
  callVisibleSetter?: (visible: boolean) => void
  visibilityTrigger?: TVisibilityTrigger
  useOwnContext?: boolean
}

export const useCloseByClickHandler = ({
  visible,
  rootRef,
  callVisibleSetter,
  visibilityTrigger,
  useOwnContext,
}: IUseCloseByClickHandler) => {
  const [popupAttributeData, setPopupAttributeData] = useState({})
  const { globalEventsNode } = useContext(PortalContext)
  const popupIdRef = useRef<string>(generateId('popup'))

  useEffect(() => {
    if (!useOwnContext) {
      return
    }

    const parentNode = rootRef.current?.closest(`[${POPUP_PARENT_IDS_ATTRIBUTE}]`)
    const parentTree = parentNode?.getAttribute(POPUP_PARENT_IDS_ATTRIBUTE)
    const parentsValue = [popupIdRef.current, parentTree]
      .filter(Boolean)
      .join(POPUP_PARENT_IDS_SPLITTER)

    setPopupAttributeData({
      [POPUP_PARENT_IDS_ATTRIBUTE]: parentsValue,
    })
  }, [useOwnContext, rootRef])

  useEffect(() => {
    const mousedownHandler: EventListenerOrEventListenerObject = (e: Event) => {
      if (visible) {
        const target = e.target as HTMLElement | null
        const popupNode = target?.closest(`[${POPUP_PARENT_IDS_ATTRIBUTE}]`)
        const parentIds =
          popupNode?.getAttribute(POPUP_PARENT_IDS_ATTRIBUTE)?.split(POPUP_PARENT_IDS_SPLITTER) ||
          []
        if (!parentIds.includes(popupIdRef.current)) {
          callVisibleSetter?.(false)
        }
      }
    }

    if (handleByClick(visibilityTrigger)) {
      globalEventsNode.addEventListener('mousedown', mousedownHandler, true)
    }

    return () => globalEventsNode.removeEventListener('mousedown', mousedownHandler, true)
  }, [callVisibleSetter, popupIdRef, visibilityTrigger, visible, globalEventsNode])

  return { popupAttributeData }
}
